import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  Input,
  Select,
  Row,
  Col,
  Card,
  message,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useZaloZnsTemplates } from '../../hooks/useZaloZnsTemplates';
import type { ZaloZnsTemplate } from '../../services/zalo-zns-template.service';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

export const ZaloZnsTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const { templates, loading, meta, filters, setFilters, deleteTemplates, cloneTemplate } =
    useZaloZnsTemplates();

  const handleSearch = (value: string) => {
    setSearchText(value);
    setFilters({ ...filters, q: value, page: 1 });
  };

  const handleStateFilter = (value: string | undefined) => {
    setFilters({
      ...filters,
      state: value !== undefined ? Number(value) : undefined,
      page: 1,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplates([id]);
      message.success('Xóa template thành công');
    } catch {
      message.error('Xóa template thất bại');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      await deleteTemplates(selectedRowKeys);
      setSelectedRowKeys([]);
      message.success(`Đã xóa ${selectedRowKeys.length} template`);
    } catch {
      message.error('Xóa template thất bại');
    }
  };

  const handleClone = async (id: string) => {
    try {
      const cloned = await cloneTemplate(id);
      message.success(`Đã nhân bản template thành: ${cloned.code}`);
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || 'Nhân bản template thất bại';
      message.error(errMsg);
    }
  };

  const columns: ColumnsType<ZaloZnsTemplate> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 130,
      render: (code: string) => <code style={{ fontSize: 12 }}>{code}</code>,
    },
    {
      title: 'Mã ZNS template',
      dataIndex: 'template_id',
      key: 'template_id',
      width: 140,
      render: (id: string) => (
        <Tag color="purple" style={{ fontFamily: 'monospace' }}>
          {id}
        </Tag>
      ),
    },
    {
      title: 'Tên template',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ZaloZnsTemplate) => (
        <Button
          type="link"
          onClick={() => navigate(`/zalo-zns-templates/${record._id}/view`)}
          style={{ padding: 0, fontWeight: 500 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Nội dung',
      dataIndex: 'content',
      key: 'content',
      render: (content: string) => (
        <Tooltip title={content} placement="topLeft">
          <span style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {content}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Tham số',
      dataIndex: 'params',
      key: 'params',
      width: 180,
      render: (params: string[]) =>
        params && params.length > 0 ? (
          <Space wrap size={4}>
            {params.slice(0, 3).map((p, i) => (
              <Tag key={i} color="geekblue" style={{ fontSize: 11 }}>
                {p}
              </Tag>
            ))}
            {params.length > 3 && (
              <Tag color="default" style={{ fontSize: 11 }}>
                +{params.length - 3}
              </Tag>
            )}
          </Space>
        ) : (
          <span style={{ color: '#bbb', fontSize: 12 }}>Không có</span>
        ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      key: 'state',
      width: 110,
      align: 'center',
      render: (state: number) => (
        <Tag color={state === 1 ? 'success' : 'error'}>{state === 1 ? 'Hoạt động' : 'Tắt'}</Tag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: true,
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_: any, record: ZaloZnsTemplate) => (
        <Space size={2}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/zalo-zns-templates/${record._id}/view`)}
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/zalo-zns-templates/${record._id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Nhân bản">
            <Popconfirm
              title="Nhân bản template này?"
              description={`Sẽ tạo bản copy với code: copy-${record.code}`}
              onConfirm={() => handleClone(record._id)}
              okText="Nhân bản"
              cancelText="Hủy"
            >
              <Button type="text" icon={<CopyOutlined />} />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              title="Xóa template này?"
              description="Thao tác này không thể hoàn tác."
              onConfirm={() => handleDelete(record._id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Zalo ZNS Templates
          </Title>
          <p style={{ color: '#8c8c8c', margin: '4px 0 0' }}>
            Quản lý mẫu tin nhắn ZNS của Zalo OA
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/zalo-zns-templates/add')}
        >
          Thêm template
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col flex={1}>
            <Search
              placeholder="Tìm theo tên, code, mã ZNS..."
              allowClear
              onSearch={handleSearch}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Select
              placeholder="Trạng thái"
              allowClear
              style={{ width: 150 }}
              onChange={handleStateFilter}
            >
              <Option value="1">Hoạt động</Option>
              <Option value="0">Tắt</Option>
            </Select>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchText('');
                setFilters({ page: 1, limit: 10 });
              }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Popconfirm
            title={`Xóa ${selectedRowKeys.length} template đã chọn?`}
            onConfirm={handleDeleteSelected}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Xóa {selectedRowKeys.length} template đã chọn
            </Button>
          </Popconfirm>
        </div>
      )}

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={templates}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
        pagination={{
          current: meta?.page || 1,
          pageSize: meta?.limit || 10,
          total: meta?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} template`,
          onChange: (page, pageSize) =>
            setFilters({ ...filters, page, limit: pageSize }),
        }}
        onChange={(_, __, sorter: any) => {
          if (sorter.field) {
            setFilters({
              ...filters,
              sort: sorter.field,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            });
          }
        }}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default ZaloZnsTemplateList;
